import { Link } from 'react-router-dom';
import { ideasData } from '../data/ideasData';
import SEO from '../components/SEO';
import { getBreadcrumbSchema } from '../data/schemas';

const Ideas = () => {
  return (
    <div className="animate-fade-in-down max-w-5xl mx-auto space-y-10">
      <SEO
        title="Research, Publications & Computational Ideas"
        description="Academic synthesis papers, bioacoustic neural architectures, and cognitive machine learning research by NotKoneko."
        path="/ideas"
        keywords={['Cognitive Neuroscience', 'The Forgetting Paradox', 'BirdCLEF 2026', 'Bioacoustics AI', 'Machine Learning', 'Catastrophic Forgetting', 'Koneko Research', 'NotKoneko']}
        schema={getBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Ideas & Research', url: '/ideas' }
        ])}
      />
      {/* Header Banner */}
      <div className="bg-[#090714] p-6 sm:p-8 border border-purple-500/20 rounded-3xl shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-purple-600/10 rounded-full blur-[140px] pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-pink-600/10 rounded-full blur-[140px] pointer-events-none" />
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 relative z-10">
          <div className="space-y-2 max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-950/50 border border-purple-500/30 text-purple-300 text-xs font-mono">
              <span>🧬</span> Computational Neuroscience &amp; Machine Learning Lab
            </div>
            <h1 className="text-3xl sm:text-4xl font-serif text-white tracking-wide">Research &amp; Ideas</h1>
            <p className="text-gray-300 text-sm sm:text-base leading-relaxed font-serif italic">
              — Academic synthesis papers, bioacoustic neural architectures, and theoretical cognitive frameworks bridging human psychology with machine logic.
            </p>
          </div>
          <div className="bg-[#0d0a1c] p-4 rounded-2xl border border-purple-500/20 font-mono text-center shrink-0 w-full md:w-auto shadow-lg">
            <span className="block text-2xl font-extrabold text-fuchsia-300 tracking-tight">3 Active</span>
            <span className="text-[10px] text-gray-400 uppercase tracking-wider block mt-0.5">Research Modules</span>
          </div>
        </div>
      </div>

      {/* Research Cards List */}
      <div className="space-y-6">
        {ideasData.map((item) => {
          return (
            <Link 
              key={item.id} 
              to={`/ideas/${item.id}`} 
              className="group block p-6 sm:p-8 rounded-3xl border transition-all duration-300 transform hover:-translate-y-1 shadow-xl relative overflow-hidden bg-[#090714] hover:bg-[#0e0a1a] border-purple-500/20 hover:border-fuchsia-500/50 hover:shadow-[0_0_25px_rgba(232,121,249,0.15)]"
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4 relative z-10">
                <div className="flex items-center gap-3">
                  <span className={`w-3 h-3 rounded-full ${item.dotColor} shrink-0`} />
                  <span className="text-xs font-mono px-3 py-1 bg-purple-950/60 text-fuchsia-300 border border-purple-500/40 rounded-full">
                    {item.category}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs font-mono text-gray-400">
                  <span>{item.date}</span>
                  <span>•</span>
                  <span className="px-2.5 py-0.5 rounded bg-[#0d0a1c] text-purple-300 border border-purple-500/20">
                    {item.status}
                  </span>
                </div>
              </div>

              <div className="space-y-3 relative z-10">
                <h3 className="text-xl sm:text-2xl font-bold tracking-tight transition-colors flex items-center justify-between text-white group-hover:text-fuchsia-300">
                  {item.title}
                  <span className="text-sm text-gray-500 group-hover:text-fuchsia-300 transform group-hover:translate-x-1 transition-transform">
                    →
                  </span>
                </h3>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed font-normal">
                  {item.summary}
                </p>
              </div>

              <div className="pt-4 mt-6 border-t border-purple-500/20 flex items-center justify-between text-xs font-mono relative z-10">
                <span className="text-gray-400">
                  {item.id === 'forgetting-curve-in-ai'
                    ? '📄 Includes Technical Documentation & Interactive Paper'
                    : item.id === 'birdclef-2026'
                    ? '🏆 Kaggle Competition Entry & Interactive Architecture Breakdown'
                    : '🔒 Internal Research Draft'}
                </span>
                <span className="font-semibold flex items-center gap-1 transition-colors text-fuchsia-400 group-hover:text-fuchsia-300">
                  {item.id === 'forgetting-curve-in-ai' ? 'Read Full Synthesis →' : item.id === 'birdclef-2026' ? 'View Kaggle Project →' : 'View Status →'}
                </span>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default Ideas;